import Breadcrumb from "@/components/Breadcrumb";
import BackButton from "@/components/buttons/BackButton";
import { DefaultSecondaryButton } from "@/styles/globalstyles";
import { useToast } from "@/hooks/useToast";
import { ErrorAlert } from "@/utils/alert";
import { Add } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import SaveSupplierModal from "@/components/exchange-rate/SaveSupplierModal";
import {
  Box,
  Typography,
  Button,
  TextField,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import { useEffect, useMemo, useState } from "react";
import {
  useCreateExchangeRateSupplier,
  useGetExchangeRateSuppliers,
  useUpdateExchangeRateSupplier,
} from "@/hooks/exchangeRateHooks";
import { useQueryHandler } from "@/hooks/useQueryHandler";
import type { exchangeRateSupplier } from "@/api/exchangeRateService";
import { usePermissions } from "@/hooks/usePermissions";
import { getAxiosErrorMessage } from "@/utils/healper";

const roundingModeOptions = [
  { value: "ROUND UP", label: "Round Up" },
  { value: "ROUND DOWN", label: "Round Down" },
  { value: "ROUND", label: "Round" },
];

const BankManagement = () => {
  const canEdit = usePermissions().canEdit("exchange.rate");
  const { showToast } = useToast();
  const [searchFilter, setSearchFilter] = useState<string>("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<
    exchangeRateSupplier | undefined
  >(undefined);

  const {
    data: data,
    isFetching: isLoadingSuppliers,
    isError: isErrorSuppliers,
  } = useGetExchangeRateSuppliers();

  const {
    mutate: createExchangeRateSupplier,
    isPending: isPendingCreateExchangeRateSupplier,
  } = useCreateExchangeRateSupplier();

  const {
    mutate: updateExchangeRateSupplier,
    isPending: isPendingUpdateExchangeRateSupplier,
  } = useUpdateExchangeRateSupplier();

  useQueryHandler({
    isFetching:
      isLoadingSuppliers ||
      isPendingCreateExchangeRateSupplier ||
      isPendingUpdateExchangeRateSupplier,
    isError: isErrorSuppliers,
    errorMessage: "เกิดข้อผิดพลาดในการโหลดข้อมูล Supplier",
  });

  const filteredRows = useMemo(() => {
    const sorted = [...(data ?? [])].sort((a, b) => {
      if (a.is_active === b.is_active) return a.name.localeCompare(b.name);
      return a.is_active ? -1 : 1;
    });
    if (!searchFilter) return sorted;
    return sorted.filter(
      (row) =>
        row.name.toLowerCase().includes(searchFilter.toLowerCase()) ||
        row.name_abbr.toLowerCase().includes(searchFilter.toLowerCase()),
    );
  }, [data, searchFilter]);

  useEffect(() => {
    document.title = "Exchange Rate | AMPA";
  }, []);

  const handleOpenAdd = () => {
    setSelectedSupplier(undefined);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (row: exchangeRateSupplier) => {
    console.log("Selected Supplier:", row);
    setSelectedSupplier(row);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedSupplier(undefined);
  };

  const handleAddSupplier = (data: exchangeRateSupplier) => {
    createExchangeRateSupplier(data, {
      onSuccess: () => {
        showToast(`เพิ่ม Supplier ${data.name} สำเร็จ`, "success");
      },
      onError: (e) => {
        ErrorAlert("เกิดข้อผิดพลาด", getAxiosErrorMessage(e));
      },
    });
  };

  const handleEditSupplier = (data: exchangeRateSupplier) => {
    updateExchangeRateSupplier(data, {
      onSuccess: () => {
        showToast(`แก้ไข Supplier ${data.name} สำเร็จ`, "success");
      },
      onError: (e) => {
        ErrorAlert("เกิดข้อผิดพลาด", getAxiosErrorMessage(e));
      },
    });
  };

  const columns: GridColDef<exchangeRateSupplier>[] = [
    {
      field: "name",
      headerName: "ชื่อ Supplier",
      minWidth: 200,
      flex: 0.3,
      headerClassName: "custom-header",
      renderCell: (param) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography sx={{ fontWeight: 500 }}>{param.row.name}</Typography>
        </Box>
      ),
    },
    {
      field: "name_abbr",
      headerName: "ตัวย่อ",
      minWidth: 100,
      flex: 0.1,
      headerClassName: "custom-header",
      renderCell: (param) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography sx={{ fontWeight: 600, color: "primary.main" }}>
            {param.row.name_abbr}
          </Typography>
        </Box>
      ),
    },
    {
      field: "rounding",
      headerName: "Rounding Mode",
      minWidth: 160,
      flex: 0.2,
      headerClassName: "custom-header",
      renderCell: (param) =>
        roundingModeOptions.find((o) => o.value === param.row.rounding)
          ?.label || param.row.rounding,
    },
    {
      field: "rounding_digits",
      headerName: "ทศนิยม (หลัก)",
      width: 130,
      headerClassName: "custom-header",
      headerAlign: "center",
      align: "center",
    },
    {
      field: "is_active",
      headerName: "สถานะ",
      headerAlign: "center",
      width: 100,
      headerClassName: "custom-header",
      renderCell: (param) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
          }}
        >
          <Chip
            label={param.row.is_active ? "Active" : "Inactive"}
            sx={{
              backgroundColor: param.row.is_active
                ? "success.light"
                : "error.light",
              color: "#fff",
              fontWeight: "600",
              fontSize: "0.8rem",
              width: "80px",
            }}
          />
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Action",
      headerAlign: "center",
      width: 100,
      sortable: false,
      headerClassName: "custom-header",
      renderCell: (param) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
          }}
        >
          <Tooltip title="แก้ไข">
            <IconButton
              size="small"
              color={canEdit ? "primary" : "default"}
              onClick={() => handleOpenEdit(param.row)}
              disabled={!canEdit}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ px: 3, pt: 3, pb: 1 }}>
      <Breadcrumb
        current="Exchange Rate Bank"
        previous={[{ key: "exchange.rate" }]}
      />
      <Box mt={1}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="flex-start"
          gap={2}
        >
          <BackButton to="/exchange-rate" />
          <Typography variant="body1" fontWeight={600}>
            จัดการ Supplier
          </Typography>
        </Box>
        <Box sx={{ p: 1, borderRadius: 1 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box sx={{ flexGrow: 1 }}>
              <TextField
                label="ค้นหา (ชื่อ Supplier / ตัวย่อ)"
                variant="outlined"
                size="small"
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                sx={{
                  mt: 2,
                  mb: 1,
                  width: "30%",
                  "& .MuiInputLabel-root": {
                    fontSize: "0.8rem",
                    color: "#666666",
                    "&.Mui-focused": { color: "primary.main" },
                  },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: "#cccccc" },
                    "&:hover fieldset": { borderColor: "#999999" },
                  },
                }}
              />
            </Box>
            <Box gap={1} display="flex">
              <Button
                variant="outlined"
                sx={{
                  ...DefaultSecondaryButton,
                  mt: 2,
                  alignItems: "center",
                  fontSize: "0.75rem",
                }}
                onClick={handleOpenAdd}
                disabled={!canEdit}
                size="small"
              >
                <Add /> เพิ่ม Supplier
              </Button>
            </Box>
          </Box>

          <DataGrid
            rows={filteredRows}
            columns={columns}
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
            pageSizeOptions={[10, 25, 50]}
            disableRowSelectionOnClick
            autoHeight
            sx={{
              mt: 1,
              border: "1px solid #e0e0e0",
              "& .custom-header": {
                backgroundColor: "#f5f5f5",
                fontWeight: 600,
              },
            }}
          />
        </Box>
      </Box>

      <SaveSupplierModal
        open={isModalOpen}
        handleClose={handleCloseModal}
        selectedSupplier={selectedSupplier}
        handleAddSupplier={handleAddSupplier}
        handleEditSupplier={handleEditSupplier}
      />
    </Box>
  );
};

export default BankManagement;
